import React, { useEffect, useState } from "react"
import toast from "react-hot-toast"

import { Modal } from "components/Modal"
import { AutoComplete } from "components/AutoComplete"
import { Button } from "components/Button"
import { DataTable } from "components/DataTable"
import { TextInput } from "components/TextInput"
import useAsyncSelect from "hooks/useAsyncSelect"
import {
   GetProductionLinesListing,
   MigrateTimelineOrders,
   MigrateOrders,
} from "api/api.service"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import { TimeLineOrderStatus } from "constants"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import { updateObjCheckBox } from "utils/commonUtils"
import ProductionLineStatus from "constants/ProductionLineStatus"

const COLUMNS = {
   SELECT: "Select",
   SLOT_ID: "Slot ID",
   TRAILER_TYPE: "Trailer Type",
   CURRENT_PRODUCTION_LINE: "Current Production Line",
   DATE_ADDED: "Date Added",
   ESTIMATED_DATE: "Estimated Date",
   PRIORITY: "Priority",
   STATUS: "Status",
}

const DATA_KEY = "data"
const TOTAL_KEY = "count"

const BulkUpdateProductionLineModal = ({
   isOpen,
   onClose,
   selectedTrailers,
   request,
   setSelectedTrailers,
   setIsSelectAll,
}) => {
   const [selectedProductionLine, setSelectedProductionLine] = useState(null)
   const [isSubmitting, setIsSubmitting] = useState(false)
   const [estimationData, setEstimationData] = useState(null)
   const [showEstimationResults, setShowEstimationResults] = useState(false)
   const [modalSelectedTrailers, setModalSelectedTrailers] = useState([])

   const {
      selectEntity: productionListing,
      handleSearchOnOptions,
      handleSelectAOption,
      handleGetSelectEntityData,
   } = useAsyncSelect({
      apiFunc: GetProductionLinesListing,
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      labelKey: "name",
      valueKey: "id",
      deps: [],
      params: {
         status: ProductionLineStatus.Active,
      },
      attachSignal: true,
   })

   // Helper function to check if all selected trailers belong to the same production line
   const getCurrentProductionLineId = () => {
      if (modalSelectedTrailers.length === 0) return null

      const firstProductionLineId = modalSelectedTrailers[0]?.productionLineId

      // Check if all trailers have the same production line ID
      const allSameProductionLine = modalSelectedTrailers.every(
         trailer => trailer.productionLineId === firstProductionLineId
      )

      return allSameProductionLine ? firstProductionLineId : null
   }

   // Get filtered production line options (exclude current production line if all trailers belong to same one)
   const getFilteredProductionLineOptions = () => {
      const currentProductionLineId = getCurrentProductionLineId()

      if (!currentProductionLineId || !productionListing?.data) {
         return productionListing?.data || []
      }

      // Filter out the current production line
      return productionListing.data.filter(
         productionLine => productionLine.id !== currentProductionLineId
      )
   }

   // Initialize modal selected trailers when modal opens
   useEffect(() => {
      if (isOpen && selectedTrailers.length > 0) {
         setModalSelectedTrailers(selectedTrailers)
         setSelectedProductionLine(null)
      }
   }, [isOpen, selectedTrailers])

   useEffect(() => {
      const currentProductionLineId = getCurrentProductionLineId()
      const filteredOptions = getFilteredProductionLineOptions()

      if (selectedProductionLine && currentProductionLineId) {
         const isStillValid = filteredOptions.some(
            option => option.id === selectedProductionLine.id
         )

         if (!isStillValid) {
            setSelectedProductionLine(null)
         }
      }
   }, [modalSelectedTrailers, productionListing?.data, selectedProductionLine])

   const handleSearch = (value = "") => {
      handleSearchOnOptions(value)
   }

   const handleCheckBoxSelect = (e, item) => {
      const dataTemp = updateObjCheckBox(item, modalSelectedTrailers, "id")
      setModalSelectedTrailers(dataTemp)
   }

   const handleConfirmMove = async () => {
      if (!selectedProductionLine || modalSelectedTrailers.length === 0) return

      setIsSubmitting(true)

      try {
         const timelineOrderIds = modalSelectedTrailers.map(
            trailer => trailer.id
         )
         const destinationProductionLineId = selectedProductionLine.id

         // Step 1: Get estimation
         const estimationResponse = await MigrateTimelineOrders({
            timelineOrderIds,
            destinationProductionLineId,
         })

         setEstimationData(estimationResponse)

         // Check if we can proceed directly to migration
         const hasIssues =
            estimationResponse?.failedMigrations?.length > 0 ||
            estimationResponse?.alreadyPresentOrderIds?.length > 0

         if (
            !hasIssues &&
            estimationResponse?.successfulEstimations?.length > 0
         ) {
            // No issues, proceed directly to migration
            await handleFinalMigration()
         } else {
            // Show estimation results for user to review
            setShowEstimationResults(true)
         }
      } catch (error) {
         toast.error("Failed to check migration. Please try again.")
      } finally {
         setIsSubmitting(false)
      }
   }

   const handleFinalMigration = async () => {
      if (!selectedProductionLine || modalSelectedTrailers.length === 0) return

      setIsSubmitting(true)

      try {
         const timelineOrderIds = modalSelectedTrailers.map(
            trailer => trailer.id
         )
         const destinationProductionLineId = selectedProductionLine.id

         // Step 2: Perform actual migration
         const migrationResponse = await MigrateOrders({
            timelineOrderIds,
            destinationProductionLineId,
         })

         // Handle migration response
         if (migrationResponse.migratedOrderIds?.length > 0) {
            toast.success(
               `Successfully migrated ${migrationResponse.migratedOrderIds.length} orders!`
            )
         }

         if (migrationResponse.alreadyPresentOrderIds?.length > 0) {
            toast.info(
               `${migrationResponse.alreadyPresentOrderIds.length} orders were already in target production line.`
            )
         }

         // Reset and close
         setSelectedProductionLine(null)
         setEstimationData(null)
         setShowEstimationResults(false)
         setModalSelectedTrailers([])
         onClose()
         setSelectedTrailers([])
         setIsSelectAll(false)
         request()
      } catch (error) {
      } finally {
         setIsSubmitting(false)
      }
   }

   const handleBackToReselect = () => {
      setShowEstimationResults(false)
      // Keep estimationData to show warning icons in step 1
   }

   const handleClose = () => {
      setSelectedProductionLine(null)
      setEstimationData(null)
      setShowEstimationResults(false)
      setModalSelectedTrailers([])
      setSelectedTrailers([])
      setIsSelectAll(false)
      onClose()
   }

   // Check if we should continue with migration (only successful estimations)
   const shouldContinueMigration = () => {
      return (
         estimationData?.successfulEstimations?.length > 0 &&
         (!estimationData?.failedMigrations?.length ||
            estimationData.failedMigrations.length === 0) &&
         (!estimationData?.alreadyPresentOrderIds?.length ||
            estimationData.alreadyPresentOrderIds.length === 0)
      )
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleClose}
         sizeClasses="10"
         modalTitle="Move Trailer"
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="header-container"
         className="bulk-update-production-line-modal"
      >
         <div className="p-4">
            {!showEstimationResults ? (
               // Step 1: Initial modal with production line selection
               <>
                  <div className="mb-4">
                     <label className="form-label">Production Line</label>
                     <div style={{ width: "33%" }}>
                        <AutoComplete
                           placeholder="Select Production Line"
                           isClearable
                           value={selectedProductionLine}
                           customStyles={autoCompleteStyles.STYLE_EIGHT}
                           onInputChange={val => handleSearch(val)}
                           options={getFilteredProductionLineOptions()}
                           onChange={val => setSelectedProductionLine(val)}
                           closeMenuOnSelect={true}
                           isLoading={productionListing?.fetching}
                           classNamePrefix="status-header-search-admin"
                           menuPortalTarget={document.body}
                           menuPosition="fixed"
                           menuPlacement="auto"
                           handleScroll={() => {
                              if (productionListing?.isDataAvailable) {
                                 handleGetSelectEntityData(
                                    productionListing?.page
                                 )
                              }
                           }}
                        />
                     </div>
                  </div>

                  <div className="mb-4">
                     <DataTable
                        data={modalSelectedTrailers}
                        loading={false}
                        tableClasses="ad-inventory-request-table"
                        config={[
                           {
                              title: COLUMNS.SELECT,
                              className: "checkbox-column",
                              thClass: "checkbox-header",
                              tdClass: "checkbox-column",
                              render: data => {
                                 // Check if this item had issues in previous estimation
                                 const isFailed =
                                    estimationData?.failedMigrations?.includes(
                                       data.id
                                    )
                                 const isAlreadyPresent =
                                    estimationData?.alreadyPresentOrderIds?.includes(
                                       data.id
                                    )

                                 return (
                                    <div className="d-flex align-items-center gap-2">
                                       <TextInput
                                          onClick={e =>
                                             handleCheckBoxSelect(e, data)
                                          }
                                          labelClass="mb-0"
                                          type="checkbox"
                                          inputClass="form-check-input-custom fm-table-checkboxes"
                                          checked={
                                             modalSelectedTrailers.filter(
                                                el => el.id === data.id
                                             ).length > 0
                                          }
                                       />
                                       {isFailed && (
                                          <span className="production-tooltip-container">
                                             <i className="fas fa-exclamation-triangle text-danger" />
                                             <span className="production-custom-tooltip">
                                                Targeted Production line cannot
                                                accommodate this order.
                                             </span>
                                          </span>
                                       )}
                                       {isAlreadyPresent && (
                                          <span className="production-tooltip-container">
                                             <i className="fas fa-exclamation-triangle text-warning" />
                                             <span className="production-custom-tooltip">
                                                Order is already present in the
                                                target production line.
                                             </span>
                                          </span>
                                       )}
                                    </div>
                                 )
                              },
                           },
                           {
                              title: COLUMNS.SLOT_ID,
                              render: data => {
                                 return (
                                    <div className="highlighted-col">
                                       {data?.timelineCells?.[0]?.slotId}
                                    </div>
                                 )
                              },
                           },
                           {
                              title: COLUMNS.TRAILER_TYPE,
                              render: data => data?.trailer?.trailerType,
                           },
                           {
                              title: COLUMNS.CURRENT_PRODUCTION_LINE,
                              render: data => data?.productionLine?.name,
                           },
                           {
                              title: COLUMNS.DATE_ADDED,
                              render: data =>
                                 data.createdAt
                                    ? convertDateTime({
                                         date: data.createdAt,
                                         customFormat:
                                            dateTimeFormat.americanFormat,
                                         dateOnly: true,
                                         local: true,
                                      })
                                    : "-",
                           },
                           {
                              title: COLUMNS.ESTIMATED_DATE,
                              render: data =>
                                 data.estimatedDate
                                    ? convertDateTime({
                                         date: data.estimatedDate,
                                         customFormat:
                                            dateTimeFormat.americanFormat,
                                         dateOnly: true,
                                         local: true,
                                      })
                                    : "-",
                           },
                           {
                              title: COLUMNS.PRIORITY,
                              render: data => {
                                 return (
                                    <div className="highlighted-col text-capitalize">
                                       {data?.priority?.toLowerCase()}
                                    </div>
                                 )
                              },
                           },
                           {
                              title: COLUMNS.STATUS,
                              render: data => {
                                 return (
                                    <div
                                       className={`inv-req-popup-status min-badge-size ${TimeLineOrderStatus.getLabelClass(
                                          data.status
                                       )}`}
                                    >
                                       {TimeLineOrderStatus.getDisplayTextKey(
                                          data.status
                                       )}
                                    </div>
                                 )
                              },
                           },
                        ]}
                     />
                  </div>

                  <div className="d-flex justify-content-center gap-2">
                     <Button
                        className="btn btn-secondary"
                        title="Cancel"
                        onClick={handleClose}
                        disabled={isSubmitting}
                     />
                     <Button
                        className="btn btn-primary"
                        title={"Confirm Move"}
                        onClick={handleConfirmMove}
                        isLoading={isSubmitting}
                        disabled={
                           !selectedProductionLine ||
                           modalSelectedTrailers.length === 0 ||
                           isSubmitting
                        }
                     />
                  </div>
               </>
            ) : (
               // Step 2: Show estimation results with warnings
               <>
                  <div className="mb-4">
                     <label className="form-label">Production Line</label>
                     <div style={{ width: "33%" }}>
                        <AutoComplete
                           placeholder="Select Production Line"
                           isClearable={false}
                           value={selectedProductionLine}
                           customStyles={autoCompleteStyles.STYLE_EIGHT}
                           options={getFilteredProductionLineOptions()}
                           onChange={() => {}} // No change allowed
                           closeMenuOnSelect={true}
                           isLoading={false}
                           isDisabled={true}
                           classNamePrefix="status-header-search-admin"
                           menuPortalTarget={document.body}
                           menuPosition="fixed"
                           menuPlacement="auto"
                           handleScroll={() => {
                              if (productionListing?.isDataAvailable) {
                                 handleGetSelectEntityData(
                                    productionListing?.page
                                 )
                              }
                           }}
                        />
                     </div>
                  </div>

                  <div className="mb-4">
                     <DataTable
                        data={modalSelectedTrailers}
                        loading={false}
                        tableClasses="ad-inventory-request-table"
                        rowClassName={data => {
                           const isFailed =
                              estimationData?.failedMigrations?.includes(
                                 data.id
                              )
                           const isAlreadyPresent =
                              estimationData?.alreadyPresentOrderIds?.includes(
                                 data.id
                              )
                           return isFailed || isAlreadyPresent
                              ? "table-danger"
                              : ""
                        }}
                        config={[
                           // Only show SELECT column if there are failed or already present items
                           ...(estimationData?.failedMigrations?.length > 0 ||
                           estimationData?.alreadyPresentOrderIds?.length > 0
                              ? [
                                   {
                                      title: COLUMNS.SELECT,
                                      className: "checkbox-column",
                                      thClass: "checkbox-header",
                                      tdClass: "checkbox-column",
                                      render: data => {
                                         // Check if this item had issues in previous estimation
                                         const isFailed =
                                            estimationData?.failedMigrations?.includes(
                                               data.id
                                            )
                                         const isAlreadyPresent =
                                            estimationData?.alreadyPresentOrderIds?.includes(
                                               data.id
                                            )

                                         return (
                                            <div>
                                               {isFailed && (
                                                  <span className="production-tooltip-container">
                                                     <i className="fas fa-exclamation-triangle text-danger" />
                                                     <span className="production-custom-tooltip">
                                                        Targeted Production line
                                                        cannot accommodate this
                                                        order.
                                                     </span>
                                                  </span>
                                               )}
                                               {isAlreadyPresent && (
                                                  <span className="production-tooltip-container">
                                                     <i className="fas fa-exclamation-triangle text-warning" />
                                                     <span className="production-custom-tooltip">
                                                        Order is already present
                                                        in the target production
                                                        line.
                                                     </span>
                                                  </span>
                                               )}
                                            </div>
                                         )
                                      },
                                   },
                                ]
                              : []),
                           {
                              title: COLUMNS.SLOT_ID,
                              render: data => {
                                 return (
                                    <div className="highlighted-col">
                                       {data?.timelineCells?.[0]?.slotId}
                                    </div>
                                 )
                              },
                           },
                           {
                              title: COLUMNS.TRAILER_TYPE,
                              render: data => {
                                 return <div>{data?.trailer?.trailerType}</div>
                              },
                           },
                           {
                              title: COLUMNS.DATE_ADDED,
                              render: data =>
                                 data.createdAt
                                    ? convertDateTime({
                                         date: data.createdAt,
                                         customFormat:
                                            dateTimeFormat.americanFormat,
                                         dateOnly: true,
                                         local: true,
                                      })
                                    : "-",
                           },
                           {
                              title: COLUMNS.ESTIMATED_DATE,
                              render: data =>
                                 data.estimatedDate
                                    ? convertDateTime({
                                         date: data.estimatedDate,
                                         customFormat:
                                            dateTimeFormat.americanFormat,
                                         dateOnly: true,
                                         local: true,
                                      })
                                    : "-",
                           },
                           {
                              title: COLUMNS.PRIORITY,
                              render: data => {
                                 return (
                                    <div className="highlighted-col text-capitalize">
                                       {data?.priority?.toLowerCase()}
                                    </div>
                                 )
                              },
                           },
                           {
                              title: COLUMNS.STATUS,
                              render: data => {
                                 return (
                                    <div
                                       className={`inv-req-popup-status min-badge-size ${TimeLineOrderStatus.getLabelClass(
                                          data.status
                                       )}`}
                                    >
                                       {TimeLineOrderStatus.getDisplayTextKey(
                                          data.status
                                       )}
                                    </div>
                                 )
                              },
                           },
                        ]}
                     />
                  </div>

                  <div className="d-flex justify-content-center gap-2">
                     <Button
                        className="btn btn-secondary"
                        title="Cancel"
                        onClick={handleClose}
                        disabled={isSubmitting}
                     />
                     {!shouldContinueMigration() && (
                        <Button
                           className="btn btn-primary"
                           title="Back to Reselect"
                           onClick={handleBackToReselect}
                           disabled={isSubmitting}
                        />
                     )}

                     {shouldContinueMigration() && (
                        <Button
                           className="btn btn-primary"
                           title={"Confirm Move"}
                           onClick={handleFinalMigration}
                           isLoading={isSubmitting}
                           disabled={isSubmitting}
                        />
                     )}
                  </div>
               </>
            )}
         </div>
      </Modal>
   )
}

export default BulkUpdateProductionLineModal
